import { useEffect, useRef } from 'react';
import { canvasSelectionStore } from '../stores/canvasSelectionStore';
import { useCanvasStores, materializeGroupsFromStore } from '../stores/CanvasContext';
import { globalTasksStore } from '../../stores/globalTasksStore';
import { resolveBoxSelectMatches } from '../lib/resolveBoxSelectMatches';
import { useScreenToCanvas } from './useScreenToCanvas';

type Phase = 'idle' | 'pending' | 'selecting';

function normalizeRect(x1: number, y1: number, x2: number, y2: number) {
  return {
    x: Math.min(x1, x2),
    y: Math.min(y1, y2),
    width: Math.abs(x2 - x1),
    height: Math.abs(y2 - y1),
  };
}

export function useCanvasBoxSelect(): void {
  const { containerRef, dragStore, store, uiStore } = useCanvasStores();
  const isConnectionDragging = dragStore.getSnapshot().context.connectionDrag !== null;

  const phaseRef = useRef<Phase>('idle');
  const startCanvasRef = useRef({ x: 0, y: 0 });
  const additiveRef = useRef(false);
  const isConnectionDraggingRef = useRef(isConnectionDragging);
  isConnectionDraggingRef.current = isConnectionDragging;

  const screenToCanvas = useScreenToCanvas();

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    function handlePointerDown(e: PointerEvent) {
      if (e.button !== 0) return;
      if (e.defaultPrevented) return;
      if (isConnectionDraggingRef.current) return;
      const target = e.target as HTMLElement;
      const inCanvas = target.closest('[data-canvas-canvas]');
      const isContainerBg = target === container;
      if (!inCanvas && !isContainerBg) return;
      if (target.closest('[data-connector]')) return;
      const entityEl = target.closest('[data-canvas-entity]') as HTMLElement | null;
      if (entityEl) {
        // Groups allow box select when inside the entered group
        const groupEl = target.closest('[data-canvas-group]') as HTMLElement | null;
        if (!groupEl) return;
        const enteredId = canvasSelectionStore.getSnapshot().context.enteredGroupId;
        if (groupEl.getAttribute('data-canvas-group') !== enteredId) return;
      }
      if (target instanceof SVGElement) return;
      if (document.body.style.cursor === 'grab' || document.body.style.cursor === 'grabbing') return;

      const coords = screenToCanvas(e.clientX, e.clientY);
      startCanvasRef.current = coords;
      additiveRef.current = e.shiftKey;
      phaseRef.current = 'pending';
    }

    function handlePointerMove(e: PointerEvent) {
      if (phaseRef.current === 'idle') return;

      const current = screenToCanvas(e.clientX, e.clientY);
      const dx = current.x - startCanvasRef.current.x;
      const dy = current.y - startCanvasRef.current.y;

      if (phaseRef.current === 'pending') {
        if (Math.abs(dx) + Math.abs(dy) < 5) return;
        phaseRef.current = 'selecting';
        canvasSelectionStore.send({ type: 'boxSelect.start' });
      }

      if (phaseRef.current === 'selecting') {
        const rect = normalizeRect(
          startCanvasRef.current.x,
          startCanvasRef.current.y,
          current.x,
          current.y,
        );
        dragStore.send({ type: 'boxSelectRect.set', rect });

        const ctx = store.getSnapshot().context;

        const tasksById = globalTasksStore.getSnapshot().context.tasksById;
        const matches = resolveBoxSelectMatches({
          rect,
          rawEntities: [
            ...Object.values(ctx.nodes),
            ...Object.values(ctx.connectors),
            ...Object.values(ctx.groups),
          ],
          layers: ctx.layers,
          groups: materializeGroupsFromStore(store, tasksById),
          tasksById,
          enteredGroupId: canvasSelectionStore.getSnapshot().context.enteredGroupId,
        });

        if (additiveRef.current) {
          canvasSelectionStore.send({ type: 'entity.add', entityIds: matches });
        } else {
          canvasSelectionStore.send({ type: 'entity.set', entityIds: matches });
        }
      }
    }

    function handlePointerUp() {
      if (phaseRef.current === 'pending') {
        canvasSelectionStore.send({ type: 'entity.clear' });
        canvasSelectionStore.send({ type: 'group.exit' });
      }
      if (phaseRef.current === 'selecting') {
        requestAnimationFrame(() => {
          canvasSelectionStore.send({ type: 'boxSelect.end' });
        });
      }
      phaseRef.current = 'idle';
      dragStore.send({ type: 'boxSelectRect.set', rect: null });
    }

    container.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);

    return () => {
      container.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
    };
  }, [containerRef, screenToCanvas, dragStore, store, uiStore]);
}
